<template>
    <section v-if="!(breakpoints?.isMobile && slice.primary.hideInMobile)" ref="section" class="slice slice-faq" aria-label="faq">
        <div class="slice-content" :class="{narrow: slice.variation === 'noImage'}">
            <PrismicRichText :field="slice.primary.title" class="slice-title" />
            <PrismicRichText :field="slice.primary.description" class="slice-description" />
            <OptimizedImage
                v-if="slice.variation !== 'noImage' && isFilled.image(slice.primary.image)"
                :prismic-image="slice.primary.image"
                image-class="image"
            />
            <slot name="header"></slot>
            <div class="question-answer-wrapper" :class="{fullwidth: slice.variation === 'noImage' || JSON.stringify(slice.primary.image) === '{}'}">
                <Collapsible v-for="(item, i) in slice.items" :id="asText(item.questionTitle)" :key="`slice-item-${i}`" class="item">
                    <template #header>
                        <PrismicRichText :field="item.questionTitle" class="question-title" />
                    </template>
                    <template #content>
                        <PrismicRichText :field="item.answer" class="answer" />
                    </template>
                </Collapsible>
            </div>
            <div class="link-container">
                <PrismicLink
                    v-if="isLink(slice.primary.linkText, slice.primary.link)"
                    :field="slice.primary.link"
                    class="link"
                    data-component-source="faq"
                >
                    {{ slice.primary.linkText }}
                </PrismicLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {useTemplateRef} from 'vue';
    import type {Content} from '@prismicio/client';
    import {asText, isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText, PrismicLink} from '@prismicio/vue';
    import {useComponentTracking} from '#imports';
    import Collapsible from '../../components/Collapsible.vue';
    import OptimizedImage from '../../components/OptimizedImage.vue';
    import {useBreakpoints} from '../../composables/shared/useBreakpoints';
    import {usePrismicFieldHelpers} from '../../composables/shared/usePrismicFieldHelpers';

    defineProps({...getSliceComponentProps<Content.FaqSliceSlice>()});
    const {isLink} = usePrismicFieldHelpers();
    const breakpoints = useBreakpoints();
    const section = useTemplateRef('section');

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-faq {
        .slice-content {
            font-family: var(--joy-font-family-base);
            display: grid;
            grid-template-columns: 31.25% 1fr;
            column-gap: var(--joy-core-spacing-12);
            row-gap: var(--joy-core-spacing-6);

            &.narrow {
                max-width: 730px;
                margin-left: auto;
                margin-right: auto;
            }

            @include mq.screen_sm(max) {
                display: flex;
                flex-direction: column;
                justify-content: center;
            }
        }

        .slice-title,
        .slice-description {
            grid-column-end: span 2;
        }

        .link-container {
            grid-column-end: span 2;
            display: flex;
            justify-content: center;
        }

        .image {
            margin: var(--joy-core-spacing-8) auto 0;
            width: 100%;
            max-width: 640px;
            height: auto;
            object-fit: contain;
            object-position: top left;

            @include mq.screen_sm(max) {
                order: 5;
                margin-top: var(--joy-core-spacing-6);
            }
        }

        .question-answer-wrapper {
            margin-top: var(--joy-core-spacing-8);
            width: 100%;
            display: flex;
            flex-direction: column;
            text-align: left;
            // gap: 32px;

            &.fullwidth {
                grid-column-end: span 2;
            }

            @include mq.screen_sm(max) {
                margin-top: 24px;
            }
        }

        .item {
            border-bottom: 1px solid var(--joy-color-neutral-30);

            &:last-child {
                border: none;
            }

            > button {
                padding: var(--joy-core-spacing-6) 0;
            }

            .question-title {
                margin-right: var(--joy-core-spacing-4);

                h2,
                h3 {
                    font-family: var(--joy-font-family-title);
                    color: var(--joy-color-neutral-40);
                    font-size: var(--joy-font-size-primary-600);
                    text-align: left;
                }
            }

            .answer {
                margin-bottom: var(--joy-core-spacing-7);
                line-height: var(--joy-line-height-large);
                font-size: var(--joy-font-size-primary-400);
                color: var(--joy-color-text-default);

                h3 {
                    font-size: var(--joy-font-size-primary-400);
                }

                a,
                a:visited {
                    color: var(--joy-color-secondary-30);
                    font-weight: var(--joy-font-weight-bold);
                }

                a:hover {
                    text-decoration: underline;
                }

                p:not(:first-child) {
                    margin-top: var(--joy-core-spacing-4);
                }

                ul,
                ol {
                    margin: var(--joy-core-spacing-8) var(--joy-core-spacing-4);
                    margin-right: 0;
                }
            }
        }
    }
</style>
